import React, { useLayoutEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { useGetOpenPortfolioHoldingDetails } from "api/holdings/useGetOpenPortfolioHoldingDetails";
import { useGetPortfolioHoldingDetails } from "api/holdings/useGetPortfolioHoldingDetails";
import { useGetStakingHoldingDetails } from "api/holdings/useStakingDetails";
import { useGetContactInfo } from "api/initial/useGetContactInfo";
import {
  OPEN_TRANSACTION_QUERY,
  TRANSACTIONS_QUERY,
  useGetBuyDataWithOpenOrders,
} from "api/trading/useGetBuyDataWithOpenOrderStatus";
import { TradableSecurity } from "api/trading/useGetTradebleSecurities";
import classNames from "classnames";
import { usePortfoliosAccountsState } from "components/MoneyModals/usePortfoliosAccountsState";
import { useWithdrawablePortfolioSelect } from "components/MoneyModals/WithdrawModalContent/useWithdrawablePortfolioSelect";
import { Select } from "components/Select/Select";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { useGetContactIdData } from "providers/ContactIdProvider";
import { BASE_PAPER_CLASS } from "./Paper";

interface WithdrawOptionProps {
  portfolioId: number;
  label: string;
  showSecurityBalance?: TradableSecurity | null;
}

const WithdrawOption = ({
  portfolioId,
  label,
  showSecurityBalance,
}: WithdrawOptionProps) => {
  const { t } = useModifiedTranslation();

  const { data: { availableCash = 0 } = {}, loading } =
    useGetBuyDataWithOpenOrders(portfolioId.toString());

  const { accountsLoading, ...cashAccountSelectProps } =
    usePortfoliosAccountsState(portfolioId);
  const { currentCashAccount: { currency = "EUR" } = {} } =
    cashAccountSelectProps;

  const { data: { amount: holdingAmount = 0 } = {} } =
    useGetPortfolioHoldingDetails(
      portfolioId.toString(),
      showSecurityBalance?.id?.toString(),
      false
    );

  const { totalOpenSellOrders } = useGetOpenPortfolioHoldingDetails(
    portfolioId.toString(),
    showSecurityBalance?.securityCode
  );

  const { totalStakeAmount } = useGetStakingHoldingDetails(
    portfolioId.toString(),
    showSecurityBalance?.securityCode
  );

  const availableHoldingAmount =
    holdingAmount - totalOpenSellOrders - totalStakeAmount;

  return (
    <div className="flex flex-col gap-1 text-sm sm:text-base">
      <div className="text-lg font-bold">{label}</div>
      <div>
        {t("withdrawFiat.availableCash")}
        {": "}
        <span
          className={classNames("font-bold", {
            "text-[#008000]": !loading,
            "text-gray-600": loading,
          })}
        >
          {loading
            ? "Loading..."
            : t("numberWithCurrency", {
                value: availableCash,
                currency: currency,
              })}
        </span>
      </div>
      {!loading && !!showSecurityBalance && (
        <div>
          {/* TODO: Add translation key "withdrawFiat.availableHoldings" */}
          {t("withdrawFiat.availableHoldings")}
          {": "}
          <span className="font-bold">
            <span
              className={classNames("font-bold", {
                "text-[#008000]": !loading,
                "text-gray-600": loading,
              })}
            >
              {availableHoldingAmount}{" "}
            </span>
            <span
              className={classNames("font-bold", {
                "text-[#008000]": !loading,
                "text-gray-600": loading,
              })}
            >
              {showSecurityBalance.securityCode.toUpperCase()}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};

interface UsePortfolioSelectProps {
  showSecurityBalance?: TradableSecurity | null;
}

export const usePortfolioSelect = ({
  showSecurityBalance,
}: UsePortfolioSelectProps = {}) => {
  const { selectedContactId } = useGetContactIdData();

  const {
    data: { portfolios, juridical: { name: juridicalName } = { name: "" } } = {
      portfolios: [],
    },
  } = useGetContactInfo(false, selectedContactId);

  const portfolioSelectProps = useWithdrawablePortfolioSelect();
  const {
    portfolioId,
    portfolioOptions: unprocessedOpts,
    setPortfolioId,
  } = portfolioSelectProps;

  const portfolioOptions: PortfolioOption[] = React.useMemo(() => {
    return unprocessedOpts.map((portfolio) => ({
      id: portfolio.id,
      label: (
        <WithdrawOption
          portfolioId={portfolio.id}
          label={portfolio.label}
          showSecurityBalance={showSecurityBalance}
        />
      ),
    }));
  }, [unprocessedOpts, showSecurityBalance]);

  useLayoutEffect(() => {
    if (!portfolioId && portfolioOptions.length > 0) {
      setPortfolioId(portfolioOptions[0].id);
    }
  }, [portfolioOptions, portfolioId, setPortfolioId]);

  const selectedPortfolio = React.useMemo(() => {
    return portfolios.find((portfolio) => portfolio.id === portfolioId);
  }, [portfolios, portfolioId]);

  const selectedPortfolioOpt = portfolioOptions.find(
    (portfolio) => portfolio.id === portfolioId
  );

  const client = useApolloClient();

  const refetchQueries = React.useCallback(() => {
    client.refetchQueries({
      include: [TRANSACTIONS_QUERY, OPEN_TRANSACTION_QUERY],
    });
  }, [client]);

  return {
    selectedPortfolio,
    setPortfolioId,
    portfolioOptions,
    portfolioId,
    selectedPortfolioOpt,
    portfolios,
    juridicalName,
    refetchQueries,
  };
};

export interface PortfolioOption {
  id: number;
  label: JSX.Element;
}

interface PortfolioSelectProps {
  value: PortfolioOption | null;
  onChange: (portfolio: PortfolioOption) => void;
  options: PortfolioOption[];
  className?: string;
}

export const PortfolioSelect = ({
  value,
  onChange,
  options,
  className,
}: PortfolioSelectProps) => {
  return (
    <Select
      value={value || { id: -1, label: <div>--</div> }}
      onChange={onChange}
      options={options}
      buttonClassName={classNames(
        BASE_PAPER_CLASS,
        "h-[80px] md:h-[100px]",
        className
      )}
      labelClassName="text-xl md:text-2xl !whitespace-normal"
    />
  );
};
